// @flow
import { ChoferPartialModel } from 'app/chofer/models/Chofer';
import { MUIDataTableColumnDef } from 'mui-datatables';
import * as React from 'react';
import { Table } from 'shared/components';

import { TransferReservationPendingModel } from '../../pendiente/models/TransferReservationPending';
import { ViajeTrasladoPendienteTableTimesCell } from '../../pendiente/components/ViajeTrasladoPendienteTableTimesCell';
import { useResponsiveBreakpoint } from '@vadiun/react-hooks';
import { formatNumberToPrice } from 'shared/helpers';
import moment from 'moment';

import PassengersItem from 'app/viaje/components/shared/passengers';
import Client from '../../coordinar/components/list/client';
import Points from '../../coordinar/components/list/points';
import ServiceType from '../../coordinar/components/list/serviceType';
import MarkAsViewed from './MarkAsViewed';
import { useSnackbar } from 'notistack';

interface Props {
  travels: any[];
  onLocationsDetails: (reservation: TransferReservationPendingModel) => void;
  onShowDriverDetails: (driver: ChoferPartialModel) => void;
  onMarkTravelCancelCheck: (id: number) => Promise<boolean>;
  highlightedId: number | null;
  handlePageChange: (currentPage: number) => void;
}

export function ViajeTrasladoCanceledTable({
  travels,
  onLocationsDetails,
  onShowDriverDetails,
  onMarkTravelCancelCheck,
  highlightedId,
  handlePageChange
}: Props) {
  const display = useResponsiveBreakpoint();
  const snackbar = useSnackbar();

  const prevLengthRef = React.useRef(travels.length);

  React.useEffect(() => {
    if (travels.length === 0) {
      return;
    }

    const prevLength = prevLengthRef.current;
    const newLength = travels.length;

    if (newLength > prevLength) {
      snackbar.enqueueSnackbar('Nuevo viaje recibido', { variant: 'info' });
    }

    prevLengthRef.current = newLength;
  }, [travels, snackbar]);

  const isMobileOrTablet =
    display === 'xs' || display === 'sm' || display === 'md';

  const checkServiceType = (val: string) => (val === 'VIP' ? 1 : 0);

  const highlightRow = (id: number, children: React.ReactNode) => (
    <div
      className={
        id === highlightedId ? 'bg-yellow-100 transition-all duration-500' : ''
      }
    >
      {children}
    </div>
  );

  const onMarkAsReady = React.useCallback(
    async (id: number) => {
      try {
        const response = await onMarkTravelCancelCheck(id);
        if (response === true) {
          snackbar.enqueueSnackbar('Viaje marcado como visto!', {
            variant: 'success'
          });
        }
      } catch (error) {}
    },
    [onMarkTravelCancelCheck, snackbar]
  );

  const data = (travels ?? [])
    .map((reservation) => ({
      ...reservation,
      locations: reservation.locations.map((location) => ({
        ...location,
        address: {
          calle: location.address.street,
          numero: location.address.streetNumber,
          localidad: location.address.locality,
          provincia: location.address.province,
          departamento: location.address.apartment,
          piso: location.address.floor,
          latitud: location.address.latitude,
          longitud: location.address.longitude
        }
      })),
      schedule: {
        arrival: reservation.reservationDatetime,
        exit: reservation.departureDatetime,
        end: reservation.arrivalDateTime
      },
      passengers: reservation.locations
        .map((location) =>
          location.passengers.map((passenger) => passenger.name)
        )
        .flat()
    }))
    .sort((a: any, b: any) => {
      if (
        moment(a.departureDatetime).format('DD-MM HH:mm') ===
        moment(b.departureDatetime).format('DD-MM HH:mm')
      ) {
        return (
          b.hasPriority - a.hasPriority ||
          checkServiceType(b.serviceType) - checkServiceType(a.serviceType)
        );
      } else {
        return a.departureDatetime - b.departureDatetime;
      }
    });

  const columns: MUIDataTableColumnDef[] = [
    {
      name: 'schedule',
      label: isMobileOrTablet ? ' ' : 'Horarios',
      options: {
        customBodyRender: (schedule, { rowIndex: index }) => {
          const travel = data[index];
          return highlightRow(
            travel.id,
            <div className="flex justify-between text-sm">
              {isMobileOrTablet ? <p>Horarios</p> : null}
              <ViajeTrasladoPendienteTableTimesCell
                schedule={schedule}
                priceDriver={`$${formatNumberToPrice(
                  travel?.estimated_prices?.driver_price ?? 0
                )}`}
              />
            </div>
          );
        }
      }
    },
    {
      name: 'cliente',
      label: isMobileOrTablet ? ' ' : 'Cliente',
      options: {
        customBodyRender: (value, { rowIndex: index }) => {
          const current = data[index];
          return highlightRow(
            current.id,
            <Client
              current={current}
              isMobileOrTablet={isMobileOrTablet}
              isCoordinatePage
            />
          );
        }
      }
    },
    {
      name: 'puntos',
      label: isMobileOrTablet ? ' ' : 'Domicilios',
      options: {
        customBodyRender: (value, { rowIndex: index }) => {
          const travel = data[index];
          return highlightRow(
            travel.id,
            <Points
              data={data}
              index={index}
              isMobileOrTablet={isMobileOrTablet}
              onLocationsDetails={onLocationsDetails}
              showInfo={false}
            />
          );
        }
      }
    },
    {
      name: 'coordinationObservation',
      label: isMobileOrTablet ? ' ' : 'Obs. Coordinación',
      options: {
        customBodyRender: (value = '', { rowIndex: index }) => {
          const travel = data[index];
          return highlightRow(
            travel.id,
            <div className="flex justify-between text-sm w-32">
              {isMobileOrTablet ? <p>Obs. Coordinación</p> : null}
              <p className="text-xs">{value}</p>
            </div>
          );
        }
      }
    },
    {
      name: 'general_observation',
      label: isMobileOrTablet ? ' ' : 'Obs. Chofer',
      options: {
        customBodyRender: (value = '', { rowIndex: index }) => {
          const travel = data[index];
          return highlightRow(
            travel.id,
            <div className="flex justify-between text-sm w-32">
              {isMobileOrTablet ? <p>Obs. Chofer</p> : null}
              <p className="text-xs">{value || ''}</p>
            </div>
          );
        }
      }
    },
    {
      name: 'serviceType',
      label: isMobileOrTablet ? ' ' : 'Servicio',
      options: {
        customBodyRender: (value = '', { rowIndex: index }) => {
          const travel = data[index];
          return highlightRow(
            travel.id,
            <ServiceType
              data={data}
              index={index}
              isMobileOrTablet={isMobileOrTablet}
            />
          );
        }
      }
    },
    {
      name: 'event_name',
      label: 'Evento',
      options: {
        setCellHeaderProps: () => ({
          style: { textAlign: 'center' }
        }),
        customBodyRender: (value = '', { rowIndex: index }) => {
          const travel = data[index];
          return highlightRow(
            travel.id,
            <p className="text-gray-700 w-36 text-sm text-center">
              {data[index].event_name}
            </p>
          );
        }
      }
    },
    {
      name: 'id',
      label: isMobileOrTablet ? ' ' : 'ID',
      options: {
        customBodyRender: (value = '', { rowIndex: index }) => {
          const travel = data[index];
          return highlightRow(
            travel.id,
            <div className="flex justify-between text-sm">
              {isMobileOrTablet ? <p>ID</p> : null}
              <p className="text-xs">{value}</p>
            </div>
          );
        }
      }
    },
    {
      name: 'states',
      label: isMobileOrTablet ? ' ' : 'Ultimo estado',
      options: {
        customBodyRender: (value = '', { rowIndex: index }) => {
          const travel = data[index];
          const lastStateBeforeCanceled =
            Array.isArray(travel.states) && travel.states.length >= 2
              ? travel.states.at(-2)
              : null;

          return highlightRow(
            travel.id,
            <div className="flex justify-between text-sm">
              {isMobileOrTablet ? <p>Ultimo estado</p> : null}
              {/* Un tag en tailwind gris claro */}
              <span className="inline-flex items-center px-3 py-1 text-xs font-medium text-gray-800 bg-gray-200 rounded-full">
                {lastStateBeforeCanceled?.type}
              </span>
              {/* <p className="text-xs">{lastStateBeforeCanceled.type}</p> */}
            </div>
          );
        }
      }
    },
    {
      name: '',
      label: isMobileOrTablet ? ' ' : 'Chofer',
      options: {
        customBodyRender: (value = '', { rowIndex: index }) => {
          const travel = data[index];
          const hasAssignedDriver =
            'assigned_driver' in travel && !!travel.assigned_driver;
          const driver = hasAssignedDriver
            ? travel.assigned_driver
            : travel.pre_assigned_driver;

          return highlightRow(
            travel.id,
            <div className="flex justify-between text-sm">
              {isMobileOrTablet ? <p>Chofer</p> : null}
              <div className="flex flex-col items-center text-sm">
                <p>{driver?.code}</p>
                <p className="w-28 text-center text-xs">{driver?.name}</p>
              </div>
            </div>
          );
        }
      }
    },
    {
      name: 'passengers',
      label: isMobileOrTablet ? ' ' : 'Pasajeros',
      options: {
        download: false,
        customBodyRender: (value, { rowIndex: index }) => {
          const travel = data[index];
          const passengers = travel?.passengers;
          return highlightRow(
            travel.id,
            <div className="flex justify-between text-sm">
              {isMobileOrTablet ? <p>Pasajeros</p> : null}
              <PassengersItem
                passengers={passengers}
                idTravel={data[index].id}
              />
            </div>
          );
        }
      }
    },
    {
      name: 'actions',
      label: 'Acciones',
      options: {
        filter: false,
        customBodyRender: (value, { rowIndex: index }) => {
          const current = data[index];
          return highlightRow(
            current.id,
            <MarkAsViewed
              onMarkTravelCancelCheck={() => onMarkAsReady(current.id)}
            />
          );
        }
      }
    }
  ];

  return (
    <div className="w-full">
      <Table
        title={null}
        options={{
          elevation: 0,
          search: false,
          filter: false,
          sort: false,
          download: false,
          print: false,
          rowsPerPageOptions: [400, 500],
          rowsPerPage: 400,
            onChangePage: handlePageChange,
        }}
        columns={columns}
        data={data}
      />
    </div>
  );
}
